import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import Select from 'react-select';

import ControlledError from '../ControlledError';
import {
  ValueContainer,
  Menu,
  Option,
  DropdownIndicator,
  ClearIndicator,
  SelectContainer,
  SelectedChips,
  SelectedValuesModal,
} from './CustomComponents';

import { STYLE } from 'constants/style';

export interface IMultiSelectProps {
  selectedValues: any;
  error?: string;
  required?: boolean;
  skippable?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  isLoading?: boolean;
  options: { value: any; label: string; [key: string]: any }[];
  disabled?: boolean;
  menuPortalElement?: any;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  hasCustomContainerForAllSelected?: boolean;
  hasSelectAllOption?: boolean;
  allSelectedLabel?: string;
  multiSelectClass?: string;
  errorClass?: string;
  parentClass?: string;
  testId?: string;
  CustomMenuListComponent?: React.ComponentType<any>;
  CustomOptionComponent?: React.ComponentType<any>;
  getOptionLabel?: (option: any) => string;
  controlStyleGenerator?: (baseStyles: any, state: any) => any;
  menuPortalStyleGenerator?: (baseStyles: any, state: any) => any;
  menuStyleGenerator?: (baseStyles: any, state: any) => any;
  menuListStyleGenerator?: (baseStyles: any, state: any) => any;
  valueContainerStyleGenerator?: (baseStyles: any, state: any) => any;
  singleValueStyleGenerator?: (baseStyles: any, state: any) => any;
  inputStyleGenerator?: (baseStyles: any, state: any) => any;
  onChange: (selectedValues: any) => void;
  onBlur?: (e?: any) => void;
  onInputChange?: (value: string) => void;
  isOptionSelected?: (option: { value: any; label: string; [key: string]: any }) => boolean;
  isOptionDisabled?: (option: { value: any; label: string; [key: string]: any }) => boolean;
}

const MultiSelect = ({
  selectedValues,
  error,
  required,
  skippable,
  placeholder,
  isClearable,
  isLoading,
  options,
  disabled,
  menuPortalElement,
  menuPlacement,
  hasCustomContainerForAllSelected,
  hasSelectAllOption,
  allSelectedLabel,
  multiSelectClass,
  errorClass,
  parentClass,
  testId,
  CustomOptionComponent,
  CustomMenuListComponent,
  getOptionLabel,
  controlStyleGenerator,
  menuPortalStyleGenerator,
  menuStyleGenerator,
  menuListStyleGenerator,
  valueContainerStyleGenerator,
  singleValueStyleGenerator,
  inputStyleGenerator,
  onChange,
  onBlur,
  onInputChange,
  isOptionSelected,
  isOptionDisabled,
}: IMultiSelectProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const menuContentRef = useRef<HTMLDivElement>(null);

  const selectableOptions = isOptionDisabled ? options.filter(o => !isOptionDisabled(o)) : options;

  // Handle ALL menu closing via document-level listeners (capture phase).
  // This completely bypasses react-select's unpredictable onMenuClose behavior.
  // Close when user interacts OUTSIDE the menu content (options, search, etc.).
  // Keep open when interacting inside the menu (selecting options, typing in search).
  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleInteractionOutside = (e: Event) => {
      const target = e.target as Node;
      // If the interaction is inside the menu content, keep menu open
      if (menuContentRef.current?.contains(target)) {
        return;
      }
      // Otherwise close (covers: control area toggle, outside clicks)
      setIsMenuOpen(false);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleInteractionOutside, true);
    document.addEventListener('touchstart', handleInteractionOutside, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handleInteractionOutside, true);
      document.removeEventListener('touchstart', handleInteractionOutside, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isMenuOpen]);

  const handleInputChange = (value, { action }) => {
    if (action !== 'set-value') {
      setInputValue(value);
      onInputChange?.(value);
    }
  };

  const handleMenuOpen = useCallback(() => {
    setInputValue('');
    setIsMenuOpen(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    // No-op: all closing is handled by the document-level capture-phase listener above.
    // This prevents react-select from unpredictably closing the menu on blur/focus changes
    // (which was the root cause of iOS issues with search bar focus and option taps).
  }, []);

  return (
    <div className={parentClass} id="select-parent" data-testid={testId}>
      <ControlledError show={!!error} error={error} styleClass={errorClass}>
        <Select
          placeholder={placeholder || ''}
          isClearable={disabled ? false : typeof isClearable === 'boolean' ? isClearable : true}
          isLoading={isLoading}
          options={options}
          onChange={onChange}
          onBlur={onBlur}
          value={selectedValues}
          defaultValue={selectedValues || []}
          isDisabled={disabled}
          isMulti={true}
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          menuPlacement={menuPlacement || 'auto'}
          menuPosition={'fixed'}
          className={multiSelectClass}
          menuPortalTarget={document.body || menuPortalElement}
          menuShouldScrollIntoView={false}
          isSearchable={false}
          backspaceRemovesValue={false}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          getOptionLabel={getOptionLabel}
          isOptionSelected={isOptionSelected}
          isOptionDisabled={isOptionDisabled}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          menuIsOpen={isMenuOpen}
          components={{
            Menu: Menu,
            ValueContainer: ValueContainer,
            Option: Option,
            DropdownIndicator: DropdownIndicator,
            ClearIndicator: ClearIndicator,
            SelectContainer: SelectContainer,
          }}
          data-testId={testId}
          data-required={required}
          data-skippable={skippable}
          data-error={!!error}
          data-menuContentRef={menuContentRef}
          data-onChange={onChange}
          data-selectableOptions={selectableOptions}
          data-allSelectedLabel={allSelectedLabel}
          data-hasCustomContainerForAllSelected={hasCustomContainerForAllSelected}
          minMenuHeight={300}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              outlineWidth: 0,
              boxShadow: 'none',
              borderColor: error
                ? `${STYLE.COLOR.ERROR} !important`
                : state.isFocused
                  ? `${STYLE.COLOR.ACCENT_PRIMARY} !important`
                  : STYLE.COLOR.INPUT_BORDER,
              borderWidth: 1,
              ...(state.isDisabled
                ? {
                    cursor: 'not-allowed !important',
                    borderColor: `${STYLE.COLOR.INPUT_BORDER} !important`,
                  }
                : { '&:hover': { borderColor: STYLE.COLOR.ACCENT_PRIMARY } }),
              minHeight: 48,
              borderRadius: 8,
              padding: '14px 20px',
              opacity: state.isDisabled ? 0.5 : 1,
              backgroundColor: error ? STYLE.COLOR.ERROR_LIGHTER : STYLE.COLOR.INPUT_BACKGROUND,
              ...(controlStyleGenerator ? controlStyleGenerator(baseStyles, state) : {}),
            }),
            menuPortal: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: 8,
              zIndex: 999999,
              ...(menuPortalStyleGenerator ? menuPortalStyleGenerator(baseStyles, state) : {}),
            }),
            menu: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: STYLE.COLOR.STROKE_DEFAULT,
              borderStyle: 'solid',
              backgroundColor: STYLE.COLOR.WHITE,
              zIndex: 999999,
              boxShadow: 'none',
              maxHeight: 'none',
              overflow: 'visible',
              ...(menuStyleGenerator ? menuStyleGenerator(baseStyles, state) : {}),
            }),
            menuList: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: '0px 0px 8px 8px',
              paddingTop: 0,
              maxHeight: '200px',
              overflowY: 'auto',
              ...(menuListStyleGenerator ? menuListStyleGenerator(baseStyles, state) : {}),
            }),
            option: (baseStyles, state) => ({
              ...baseStyles,
              minHeight: '44px',
              height: 'auto',
              padding: '10px 15px',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              fontFamily: STYLE.FONT.PRIMARY_FONT,
              backgroundColor: state.isSelected ? STYLE.COLOR.INPUT_BACKGROUND : STYLE.COLOR.WHITE,
              color: state.isDisabled ? STYLE.COLOR.TEXT2 : STYLE.COLOR.TEXT1,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              '&:hover': {
                backgroundColor: state.isDisabled ? STYLE.COLOR.WHITE : STYLE.COLOR.INPUT_BACKGROUND,
              },
              ...(state.isDisabled ? { cursor: 'not-allowed' } : {}),
            }),
            dropdownIndicator: (baseStyles, state) => ({
              ...baseStyles,
              ...(state.isDisabled ? { cursor: 'not-allowed' } : { cursor: 'pointer' }),
              padding: '0px 0px 0px 8px',
            }),
            clearIndicator: baseStyles => ({
              ...baseStyles,
              color: STYLE.COLOR.TEXT2,
              padding: '0px 0px 0px 8px',
            }),
            indicatorSeparator: baseStyles => ({
              ...baseStyles,
              color: STYLE.COLOR.TEXT2,
              display: 'none',
            }),
            loadingIndicator: baseStyles => ({
              ...baseStyles,
              color: STYLE.COLOR.TEXT1,
              padding: '8px 4px',
            }),
            loadingMessage: baseStyles => ({
              ...baseStyles,
              color: STYLE.COLOR.TEXT1,
            }),
            input: (baseStyles, state) => ({
              ...baseStyles,
              color: state.isDisabled ? STYLE.COLOR.TEXT3 : STYLE.COLOR.TEXT1,
              fontSize: '14px',
              fontWeight: 400,
              fontFamily: STYLE.FONT.PRIMARY_FONT,
              lineHeight: '20px',
              width: 1,
              ...(inputStyleGenerator ? inputStyleGenerator(baseStyles, state) : {}),
            }),
            singleValue: (baseStyles, state) => ({
              ...baseStyles,
              color: state.isDisabled ? STYLE.COLOR.TEXT3 : STYLE.COLOR.TEXT1,
              fontSize: '14px',
              fontWeight: 400,
              fontFamily: STYLE.FONT.PRIMARY_FONT,
              lineHeight: '20px',
              ...(singleValueStyleGenerator ? singleValueStyleGenerator(baseStyles, state) : {}),
            }),
            container: (baseStyles, state) => ({
              ...baseStyles,
              outlineWidth: 0,
              boxShadow: 'none',
              borderColor: error
                ? `${STYLE.COLOR.ERROR} !important`
                : state.isFocused
                  ? `${STYLE.COLOR.ACCENT_PRIMARY} !important`
                  : STYLE.COLOR.INPUT_BORDER,
              borderWidth: 1,
              ...(state.isDisabled
                ? {
                    cursor: 'not-allowed !important',
                    borderColor: `${STYLE.COLOR.INPUT_BORDER} !important`,
                  }
                : { '&:hover': { borderColor: STYLE.COLOR.ACCENT_PRIMARY } }),
              minHeight: 48,
              borderRadius: 8,
              // padding: "0px 20px 0px 12px",
              backgroundColor: error ? STYLE.COLOR.ERROR_LIGHTER : STYLE.COLOR.INPUT_BACKGROUND,
              ...(state.isDisabled ? { cursor: 'not-allowed', pointerEvents: 'auto' } : {}),
            }),
            valueContainer: (baseStyles, state) => ({
              ...baseStyles,
              overflow: 'visible',
              paddingLeft: '0 !important',
              ...(valueContainerStyleGenerator ? valueContainerStyleGenerator(baseStyles, state) : {}),
            }),
            multiValueRemove: (baseStyles, state) => ({
              ...baseStyles,
              ...(state.isDisabled ? { display: 'none' } : {}),
            }),
          }}
        />
      </ControlledError>
      {selectedValues.length > 0 && (
        <SelectedChips
          chips={selectedValues}
          onMoreClick={() => setIsModalVisible(true)}
          onRemove={({ value }) => onChange(selectedValues.filter(item => item.value !== value))}
        />
      )}
      {isModalVisible && (
        <SelectedValuesModal
          selectedValues={selectedValues}
          onClose={() => setIsModalVisible(false)}
          onSave={onChange}
        />
      )}
    </div>
  );
};

export default memo(MultiSelect);
